import { useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  InputAdornment,
  Slider,
  Typography,
} from "@mui/material";
import { FieldErrors, useWatch } from "react-hook-form";
import { useAtom } from "jotai";
import { SelectBox, TextField } from "../../../common";
import { selectedOrgIdAtom } from "../../../../atoms";
import { useFetchSkillsQuery } from "../../../../api/userService";
import { Search } from "../../../search";
import {
  CREW_EMPTY_AVAILABILITY,
  CREW_ROLE_DROPDOWN,
  CREW_ROLE_LABELS,
  CREW_ROLES_WITH_REQUIRED_PAY_RATES,
  CREW_ROLES_WITHOUT_EMAIL,
  CREW_DEFAULT_SKILL_LEVEL,
  CREW_SKILL_COLORS,
  CREW_SKILL_LEVELS,
  CREW_STATUS_DROPDOWN,
} from "../../../../constants";
import { CrewStatus, Role } from "../../../../enums";
import {
  CrewLeave,
  CrewMember,
  CrewMemberFormData,
  CrewSkill,
  CrewSkillLevel,
  ModalForm,
} from "../../../../types";
import { crewMemberFormSchema } from "../../../../validations";
import { useValidateForm } from "../../../../hooks";
import { theme } from "../../../../utils";
import { CrewAvailability } from "./CrewAvailability";
import { CrewLeaves } from "./CrewLeaves";

type CrewFormTab = "profile" | "skills" | "pay" | "leave";

const TABS: { id: CrewFormTab; label: string }[] = [
  { id: "profile", label: "Profile" },
  { id: "skills", label: "Skills" },
  { id: "pay", label: "Pay & Hours" },
];

const PROFILE_FIELDS: (keyof CrewMemberFormData)[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "roleCode",
  "status",
];

const sectionBoxSx = {
  border: "1px solid var(--dashboard-border-divider)",
  borderRadius: "var(--dashboard-radius-control)",
  overflow: "hidden",
};

const listRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  px: 1.5,
  py: 0.75,
  cursor: "pointer",
  "&:not(:last-of-type)": {
    borderBottom: "1px solid var(--dashboard-border-row)",
  },
};

type CrewFormProps = ModalForm<CrewMember, CrewMember>;

export const CrewForm = ({ id, defaultValues, handleEdit }: CrewFormProps) => {
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const { register, control, handleSubmit, errors } =
    useValidateForm<CrewMemberFormData>(crewMemberFormSchema);
  const roleCode = useWatch({
    control,
    name: "roleCode",
    defaultValue: defaultValues?.roleCode ?? Role.Washer,
  }) as Role;
  const isEmailRequired = !CREW_ROLES_WITHOUT_EMAIL.includes(roleCode);
  const isPayRateRequired =
    CREW_ROLES_WITH_REQUIRED_PAY_RATES.includes(roleCode);
  const [tab, setTab] = useState<CrewFormTab>("profile");
  const [canDrive, setCanDrive] = useState(defaultValues?.canDrive ?? false);
  const [skills, setSkills] = useState<CrewSkill[]>(
    defaultValues?.skills ?? [],
  );
  // Only leave added in this session; saved leave is loaded by CrewLeaves.
  const [leaves, setLeaves] = useState<CrewLeave[]>([]);
  // Leave can only be recorded for an existing crew member.
  const tabs = defaultValues
    ? [...TABS, { id: "leave" as CrewFormTab, label: "Leave" }]
    : TABS;
  const [availability, setAvailability] = useState(
    defaultValues?.availability ?? CREW_EMPTY_AVAILABILITY,
  );

  const [skillSearch, setSkillSearch] = useState("");
  const fetchSkillsQuery = useFetchSkillsQuery(selectedOrgId, {
    enabled: selectedOrgId ? true : false,
  });
  const filteredSkills = (fetchSkillsQuery.data ?? []).filter((skill) =>
    skill.name.toLowerCase().includes(skillSearch.trim().toLowerCase()),
  );

  const isSameSkill = (a: CrewSkill, b: CrewSkill) =>
    a.id !== undefined && b.id !== undefined
      ? a.id === b.id
      : a.name === b.name;

  const hasSkill = (skill: CrewSkill) =>
    skills.some((e) => isSameSkill(e, skill));

  const toggleSkill = (skill: CrewSkill) =>
    setSkills((prev) =>
      prev.some((e) => isSameSkill(e, skill))
        ? prev.filter((e) => !isSameSkill(e, skill))
        : [...prev, { ...skill, level: CREW_DEFAULT_SKILL_LEVEL }],
    );

  const getSkillLevel = (skill: CrewSkill) =>
    skills.find((e) => isSameSkill(e, skill))?.level ??
    CREW_DEFAULT_SKILL_LEVEL;

  const getSkillLevelIndex = (skill: CrewSkill) =>
    Math.max(
      CREW_SKILL_LEVELS.findIndex(
        (level) => level.value === getSkillLevel(skill),
      ),
      0,
    );

  const setSkillLevel = (skill: CrewSkill, level: CrewSkillLevel) =>
    setSkills((prev) =>
      prev.some((e) => isSameSkill(e, skill))
        ? prev.map((e) => (isSameSkill(e, skill) ? { ...e, level } : e))
        : [...prev, { ...skill, level }],
    );

  const handleValid = (data: CrewMemberFormData) => {
    handleEdit({
      id: defaultValues?.id ?? 0,
      employeeId: defaultValues?.employeeId ?? "",
      accountIds: defaultValues?.accountIds ?? [],
      roleName: CREW_ROLE_LABELS[data.roleCode] ?? data.roleCode,
      hoursThisWeek: defaultValues?.hoursThisWeek,
      overtime: defaultValues?.overtime,
      onLeaveToday: defaultValues?.onLeaveToday ?? false,
      ...data,
      // Empty rates are optional for some roles; save them as no rate.
      baseRate: Number.isNaN(data.baseRate) ? null : data.baseRate,
      overtimeRate: Number.isNaN(data.overtimeRate) ? null : data.overtimeRate,
      canDrive,
      skills,
      availability,
      leaves,
    });
  };

  const handleInvalid = (formErrors: FieldErrors<CrewMemberFormData>) => {
    const fields = Object.keys(formErrors) as (keyof CrewMemberFormData)[];
    setTab(
      fields.some((field) => PROFILE_FIELDS.includes(field))
        ? "profile"
        : "pay",
    );
  };

  return (
    <Box
      onSubmit={handleSubmit(handleValid, handleInvalid)}
      component={"form"}
      id={id}
      noValidate
      sx={{ py: 2 }}
    >
      <Box
        className="dashboard-segmented"
        role="tablist"
        sx={{ width: "100%" }}
      >
        {tabs.map((item) => (
          <Box
            key={item.id}
            role="tab"
            aria-selected={tab === item.id}
            className={`dashboard-segmented-item${tab === item.id ? " active" : ""}`}
            sx={{ flex: 1, px: 1 }}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Box>
        ))}
      </Box>

      {/* Profile */}
      <Grid
        container
        columnSpacing={2}
        rowSpacing={3}
        sx={{ mt: 3, display: tab === "profile" ? "flex" : "none" }}
      >
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="First Name"
            name="firstName"
            register={register}
            error={errors["firstName"]}
            defaultValue={defaultValues?.firstName}
            maxLength={25}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Last Name"
            name="lastName"
            register={register}
            error={errors["lastName"]}
            defaultValue={defaultValues?.lastName}
            maxLength={25}
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Email"
            name="email"
            register={register}
            error={errors["email"]}
            defaultValue={defaultValues?.email}
            maxLength={45}
            required={isEmailRequired}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Phone Number"
            name="phone"
            register={register}
            error={errors["phone"]}
            defaultValue={defaultValues?.phone}
            maxLength={20}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectBox
            label="Role"
            name="roleCode"
            register={register}
            error={errors["roleCode"]}
            dropdown={CREW_ROLE_DROPDOWN}
            defaultValue={defaultValues?.roleCode ?? Role.Washer}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectBox
            label="Status"
            name="status"
            register={register}
            error={errors["status"]}
            dropdown={
              defaultValues
                ? CREW_STATUS_DROPDOWN
                : CREW_STATUS_DROPDOWN.filter(
                    (status) => status.value !== CrewStatus.Inactive,
                  )
            }
            defaultValue={defaultValues?.status ?? CrewStatus.Active}
            required
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={canDrive}
                onChange={(e) => setCanDrive(e.target.checked)}
              />
            }
            label="Driver"
          />
        </Grid>
      </Grid>

      {/* Skills */}
      <Box sx={{ mt: 3, display: tab === "skills" ? "block" : "none" }}>
        <Typography className="dashboard-metric-label" sx={{ mb: 1 }}>
          Wash Skills
        </Typography>
        <Box
          sx={{ mb: 1.5 }}
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
        >
          <Search
            value={skillSearch}
            onInputChange={(e) => setSkillSearch(e.target.value)}
          />
        </Box>
        <Box sx={{ ...sectionBoxSx, maxHeight: 300, overflowY: "auto" }}>
          {filteredSkills.map((skill) => (
            <Box key={skill.id} sx={listRowSx}>
              <Box
                component="label"
                htmlFor={`crew-skill-${skill.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor:
                      CREW_SKILL_COLORS[skill.name]?.color ??
                      "var(--dashboard-text-secondary)",
                  }}
                />
                <Typography sx={{ fontSize: 14 }}>{skill.name}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Slider
                  size="small"
                  step={1}
                  marks
                  min={0}
                  max={CREW_SKILL_LEVELS.length - 1}
                  value={getSkillLevelIndex(skill)}
                  onChange={(_e, value) =>
                    setSkillLevel(
                      skill,
                      CREW_SKILL_LEVELS[value as number].value,
                    )
                  }
                  aria-label={`${skill.name} level`}
                  aria-valuetext={
                    CREW_SKILL_LEVELS[getSkillLevelIndex(skill)].key
                  }
                  sx={{
                    width: { xs: 80, sm: 120 },
                    opacity: hasSkill(skill) ? 1 : 0.4,
                  }}
                />
                <Typography
                  sx={{
                    width: 84,
                    fontSize: 12,
                    fontWeight: 600,
                    color: hasSkill(skill)
                      ? theme.palette.primary.main
                      : "var(--dashboard-text-secondary)",
                  }}
                >
                  {CREW_SKILL_LEVELS[getSkillLevelIndex(skill)].key}
                </Typography>
              </Box>
              <Checkbox
                id={`crew-skill-${skill.id}`}
                size="small"
                checked={hasSkill(skill)}
                onChange={() => toggleSkill(skill)}
              />
            </Box>
          ))}
          {filteredSkills.length === 0 && (
            <Typography
              className="dashboard-helper-text"
              sx={{ px: 1.5, py: 1.5 }}
            >
              {fetchSkillsQuery.isLoading
                ? "Loading skills…"
                : fetchSkillsQuery.isError
                  ? "Couldn't load skills"
                  : "No skills found"}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Pay & Hours */}
      <Box sx={{ mt: 3, display: tab === "pay" ? "block" : "none" }}>
        <Typography className="dashboard-metric-label" sx={{ mb: 1 }}>
          Pay Rates
        </Typography>
        <Grid container columnSpacing={2} rowSpacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              type="number"
              label="Base Rate / hr"
              name="baseRate"
              register={register}
              valueAsNumber
              error={errors["baseRate"]}
              defaultValue={defaultValues?.baseRate ?? undefined}
              required={isPayRateRequired}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              type="number"
              label="Overtime Rate / hr"
              name="overtimeRate"
              register={register}
              valueAsNumber
              error={errors["overtimeRate"]}
              defaultValue={defaultValues?.overtimeRate ?? undefined}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
        </Grid>

        <Typography className="dashboard-metric-label" sx={{ mt: 4, mb: 1 }}>
          Weekly Availability
        </Typography>
        <CrewAvailability
          variant="default"
          availability={availability}
          onChange={setAvailability}
        />
      </Box>

      {/* Leave */}
      {defaultValues && (
        <Box sx={{ mt: 3, display: tab === "leave" ? "block" : "none" }}>
          <CrewLeaves
            userId={defaultValues.id}
            leaves={leaves}
            onChange={setLeaves}
          />
        </Box>
      )}
    </Box>
  );
};
